import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import pilogo from '../assets/piRound.svg'
import { 
  Home, 
  ArrowLeft, 
  AlertTriangle, 
  Shield, 
  Zap, 
  Sparkles,
  LayoutDashboard,
  Package,
  Users,
  ShoppingBag,
  Settings
} from 'lucide-react';

const PageNotFound = () => {
  const quickLinks = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
    { icon: ShoppingBag, label: 'Orders', path: '/orders' },
    { icon: Package, label: 'Products', path: '/products' },
    { icon: Users, label: 'Users', path: '/users' },
    { icon: Settings, label: 'Settings', path: '/rate-settings' },
  ];

  return (
    <div className="min-h-screen h-screen overflow-hidden bg-[#0a0f1e] flex items-center justify-center px-3 py-2">
      
      {/* ─── Desktop Version ──────────────────────────────────────────────── */}
      <div className="hidden lg:block w-full max-w-6xl">
        <div className="grid grid-cols-2 gap-8 items-center">
          
          {/* LEFT SECTION */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="text-left"
          >
            <div className="inline-flex items-center gap-2 bg-white/5 rounded-full px-3 py-1.5 border border-white/10 mb-4">
              <Shield className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-[10px] font-medium text-amber-400 uppercase tracking-wider">Admin Panel</span>
            </div>

            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5, type: "spring" }}
              className="relative inline-block"
            >
              <h1 className="text-8xl font-extrabold bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 bg-clip-text text-transparent leading-none tracking-tight">
                404
              </h1>
              <div className="absolute -top-3 -right-6 animate-pulse">
                <Sparkles className="w-5 h-5 text-amber-400" />
              </div>
            </motion.div>

            <h2 className="text-3xl font-bold text-white mt-3 mb-2">
              Page Not Found
            </h2>
            
            <p className="text-gray-400 max-w-md">
              The page you're looking for doesn't exist in the admin panel.
            </p>

            <div className="w-16 h-1 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full my-4" />

            <div className="flex flex-wrap gap-3">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-semibold text-sm hover:shadow-lg hover:shadow-amber-500/25 transition-all duration-300 group"
              >
                <Home className="w-4 h-4 group-hover:rotate-[-8deg] transition-transform" />
                Dashboard
              </Link>
              
              <button
                onClick={() => window.history.back()}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white/5 text-gray-300 rounded-lg font-semibold text-sm border border-white/10 hover:bg-white/10 transition-all"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </motion.div>

          {/* RIGHT SECTION */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="relative flex items-center justify-center"
          >
            <div className="relative w-full max-w-sm">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-full blur-2xl" />
              
              <div className="relative bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  className="flex justify-center mb-4"
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-full flex items-center justify-center border-2 border-amber-500/30">
                    <AlertTriangle className="w-10 h-10 text-amber-400" />
                  </div>
                </motion.div>

                <div className="text-center">
                  <div className="text-4xl mb-2">🔍</div>
                  <h3 className="text-base font-bold text-white mb-1">
                    Admin Access Required
                  </h3>
                  <p className="text-xs text-gray-400">
                    Restricted page for administrators only.
                  </p>
                </div>

                <div className="grid grid-cols-5 gap-2 mt-4 pt-4 border-t border-white/10">
                  {quickLinks.map((link, index) => {
                    const Icon = link.icon;
                    return (
                      <Link
                        key={index}
                        to={link.path}
                        className="group flex flex-col items-center gap-0.5 p-1.5 rounded-lg hover:bg-white/5 transition-all"
                      >
                        <Icon className="w-4 h-4 text-gray-400 group-hover:text-amber-400 transition-colors" />
                        <span className="text-[8px] text-gray-500 group-hover:text-gray-300 transition-colors">
                          {link.label}
                        </span>
                      </Link>
                    );
                  })}
                </div>

                <div className="absolute -top-2 -right-2 w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center border border-amber-500/30">
                  <Zap className="w-5 h-5 text-amber-400" />
                </div>
                <div className="absolute -bottom-2 -left-2 w-8 h-8 bg-orange-500/20 rounded-full flex items-center justify-center border border-orange-500/30">
                  <Sparkles className="w-4 h-4 text-orange-400" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Footer Desktop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-8 text-center"
        >
          <p className="text-xs text-gray-600">
            <span className="text-amber-400 font-semibold">Sapi Admin</span> • 
            <span className="mx-1">Pi Network</span> • 
            <span className="mx-1">v2.0</span>
          </p>
        </motion.div>
      </div>

      {/* ─── Mobile Version (COMPACT) ────────────────────────────────────── */}
      <div className="lg:hidden w-full max-w-sm h-full flex items-center justify-center">
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10 w-full max-w-xs">
          
          {/* Badge */}
          <div className="flex justify-center mb-2">
            <div className="inline-flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1 border border-white/10">
              <Shield className="w-3 h-3 text-amber-400" />
              <span className="text-[8px] font-medium text-amber-400 uppercase tracking-wider">Admin</span>
            </div>
          </div>

          {/* 404 Number */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.4 }}
            className="text-center relative"
          >
            <h1 className="text-6xl font-extrabold bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 bg-clip-text text-transparent leading-none">
              404
            </h1>
            <div className="absolute -top-2 -right-2 animate-pulse">
              <Sparkles className="w-4 h-4 text-amber-400" />
            </div>
          </motion.div>

          {/* Message */}
          <div className="text-center mt-1">
            <h2 className="text-base font-bold text-white">
              Page Not Found
            </h2>
            <p className="text-[10px] text-gray-400 mt-0.5">
              This page doesn't exist in the admin panel.
            </p>
          </div>

          {/* Icon */}
          <div className="flex justify-center my-2">
            <div className="w-14 h-14 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-full flex items-center justify-center border border-amber-500/30">
              <AlertTriangle className="w-7 h-7 text-amber-400" />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 mt-2">
            <Link
              to="/"
              className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-semibold text-xs hover:shadow-lg transition-all active:scale-95"
            >
              <Home className="w-3.5 h-3.5" />
              Dashboard
            </Link>
            
            <button
              onClick={() => window.history.back()}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-white/5 text-gray-300 rounded-lg font-semibold text-xs border border-white/10 active:scale-95 transition-all"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-5 gap-1 mt-3 pt-3 border-t border-white/10">
            {quickLinks.map((link, index) => {
              const Icon = link.icon;
              return (
                <Link
                  key={index}
                  to={link.path}
                  className="group flex flex-col items-center gap-0.5 p-1.5 rounded-lg hover:bg-white/5 transition-all"
                >
                  <Icon className="w-4 h-4 text-gray-500 group-hover:text-amber-400 transition-colors" />
                  <span className="text-[7px] text-gray-600 group-hover:text-gray-400 transition-colors">
                    {link.label}
                  </span>
                </Link>
              );
            })}
          </div>

          {/* Pi Icon */}
          <div className="flex justify-center mt-2">
            <span className="text-[10px] text-gray-700">✦</span>
            <span className="text-[10px] text-amber-500/50 mx-1">✦</span>
            <span className="text-[10px] text-gray-700">✦</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageNotFound;